import { action, computed, makeObservable, observable } from 'mobx';
import toast from 'react-hot-toast';
import URLConstants from '../../constants/url-constants';
import config from '../../helpers/config-helper';

import IApiResponse, {
  IApiSuccessResponse,
} from '../../models/response/IApiResponse';

import { fixTimezoneOffsetYYYYMMDD } from '../../constants/constants';
import { IAddGroupCode } from '../../models/forms/IAddUpdateGroupCode';
import {
  IAddGroupSetting,
  IUpdateGroupSetting,
} from '../../models/forms/IAddUpdateGroupSetting';
import { IOption } from '../../models/ICommon';
import {
  IGroupNameOptions,
  IGroupSettingList,
  IGroupSettingListVM,
  IGroupSettingVM,
} from '../../models/response/IGroupSettingResponse';
import { IGroupSettingState } from '../../models/state/IGroupSettingState';
import { formatMessage } from '../../translations/format-message';
import {
  GetAllGroupSettingState,
  ObservableInitialState,
} from '../initialState';
import * as baseService from '../service/base-service';
const appConfig = config();

export class GroupSettingStore implements IGroupSettingState {
  initialStateValue = ObservableInitialState;
  groupSettingList: IGroupSettingListVM = GetAllGroupSettingState;
  groupSetting: IGroupSettingVM | undefined = undefined;
  groupSettingState = { ...this.initialStateValue };
  addUpdateGroupSettingState = { ...this.initialStateValue };
  deleteGroupSettingState = { ...this.initialStateValue };
  getAllGroupSettingState = { ...this.initialStateValue };
  getAllGroupNameSettingState = { ...this.initialStateValue };
  groupSettingOptions: IGroupNameOptions[] = [];
  getGroupSettingByNameState = { ...this.initialStateValue };
  groupDetailsByName: IGroupSettingVM[] = [];
  updateGroupSettingState = { ...this.initialStateValue };
  addGroupCodeState = { ...this.initialStateValue };

  constructor() {
    makeObservable(this, {
      groupSetting: observable,
      groupSettingState: observable,
      groupSettingList: observable,
      addUpdateGroupSettingState: observable,
      deleteGroupSettingState: observable,
      getAllGroupSettingState: observable,
      GetGroupSettingListService: action,
      GetGroupSettingByIdService: action,
      AddGroupSettingService: action,
      UpdateGroupSettingService: action,
      DeleteGroupSettingService: action,
      GetAllGroupSettingService: action,
      resetDeleteGroupSetting: action,
      resetAddUpdateGroupSettingState: action,
      reset: action,
      allGroupSettings: computed,
      groupSettingOptions: observable,
      allGroupOptions: computed,
      getGroupSettingByNameState: observable,
      groupDetailsByName: observable,
      groupsByNameList: computed,
      resetGroupSettingByNameState: action,
      resetGetAllGroupSettingState: action,
      resetGroupSettingDetail: action,
      updateGroupSettingState: observable,
      resetUpdateGroupSettingState: action,
      addGroupCodeState: observable,
      AddGroupCodeService: action,
      resetAddGroupCodeState: action,
      resetGetAllGroupNameSettingState: action,
      isGroupActive: computed,
      resetStore: action,
    });
  }

  //#region Actions
  GetGroupSettingListService = (
    pageNo: number,
    pagerSize: number,
    tenantId: number,
    filters?: string
  ) => {
    this.getAllGroupSettingState.inProgress = true;
    const url = `${URLConstants.GetAllGroupSettingList}?TenantId=${tenantId}&PageSize=${pagerSize}&PageNo=${pageNo}&Filters=${filters}`;
    return baseService
      .getRequest(url)
      .then(
        (response: IApiResponse<IApiSuccessResponse<IGroupSettingListVM>>) => {
          if (response.data.Error) {
            this.getAllGroupSettingState.error = response.data.Message;
            toast.error(formatMessage(response.data.Message));
          } else {
            this.groupSettingList = response.data.Data;
            this.getAllGroupSettingState.success = true;
          }
        }
      )
      .catch((err: string) => {
        this.getAllGroupSettingState.error = err;
      })
      .finally(
        action(() => {
          this.getAllGroupSettingState.inProgress = false;
        })
      );
  };

  AddGroupSettingService = (data: IAddGroupSetting) => {
    this.addUpdateGroupSettingState.inProgress = true;
    const url = URLConstants.AddGroupSetting;
    return baseService
      .postRequest(url, data)
      .then((response: IApiResponse<IApiSuccessResponse<boolean>>) => {
        if (response.data.Error) {
          this.addUpdateGroupSettingState.error = response.data.Message;
        } else {
          this.addUpdateGroupSettingState.success = true;
        }
      })
      .catch((err: string) => {
        this.addUpdateGroupSettingState.error = err;
      })
      .finally(
        action(() => {
          this.addUpdateGroupSettingState.inProgress = false;
        })
      );
  };

  UpdateGroupSettingService = (data: IUpdateGroupSetting) => {
    this.updateGroupSettingState.inProgress = true;
    let url = URLConstants.UpdateGroupSetting;
    return baseService
      .putRequest(url, data)
      .then((response: IApiResponse<IApiSuccessResponse<boolean>>) => {
        if (response.data.Error) {
          this.updateGroupSettingState.error = response.data.Message;
        } else {
          this.updateGroupSettingState.success = true;
        }
      })
      .catch((err: string) => {
        this.updateGroupSettingState.error = err;
      })
      .finally(
        action(() => {
          this.updateGroupSettingState.inProgress = false;
        })
      );
  };

  GetGroupSettingByIdService = (id: number) => {
    this.groupSettingState.inProgress = true;
    let url = URLConstants.GetGroupSettingById + '/' + id;
    return baseService
      .getRequest(url)
      .then((response: IApiResponse<IApiSuccessResponse<IGroupSettingVM>>) => {
        if (response.data.Error) {
          this.groupSettingState.error = response.data.Message;
        } else {
          this.groupSetting = response.data.Data;
        }
      })
      .catch((err: string) => {
        this.groupSettingState.error = err;
      })
      .finally(
        action(() => {
          this.groupSettingState.inProgress = false;
        })
      );
  };

  DeleteGroupSettingService = (id: number) => {
    this.deleteGroupSettingState.inProgress = true;
    const url = URLConstants.DeleteGroupSetting + '/' + id;
    return baseService
      .deleteRequest(url)
      .then((response: IApiResponse<IApiSuccessResponse<boolean>>) => {
        if (response.data.Error) {
          this.deleteGroupSettingState.error = response.data.Message;
          toast.error(formatMessage(response.data.Message));
        } else this.deleteGroupSettingState.success = true;
      })
      .catch((err: string) => {
        this.deleteGroupSettingState.error = err;
      })
      .finally(
        action(() => {
          this.deleteGroupSettingState.inProgress = false;
        })
      );
  };

  GetAllGroupSettingService = (tenantId: number) => {
    this.getAllGroupNameSettingState.inProgress = true;
    const url = `${URLConstants.GetAllGroupSetting}?TenantId=${tenantId}`;

    return baseService
      .getRequest(url)
      .then(
        (response: IApiResponse<IApiSuccessResponse<IGroupNameOptions[]>>) => {
          if (response.data.Error) {
            this.getAllGroupNameSettingState.error = response.data.Message;
            toast.error(formatMessage(response.data.Message));
          } else {
            this.groupSettingOptions = response.data.Data;
            this.getAllGroupNameSettingState.success = true;
          }
        }
      )
      .catch((err: string) => {
        this.getAllGroupNameSettingState.error = err;
      })
      .finally(
        action(() => {
          this.getAllGroupNameSettingState.inProgress = false;
        })
      );
  };

  GetGroupSettingByNameService = (tenantId: number, GroupName: string) => {
    this.getGroupSettingByNameState.inProgress = true;
    let url = `${URLConstants.GetGroupSettingByName}?TenantId=${tenantId}&GroupName=${GroupName}`;
    return baseService
      .getRequest(url)
      .then(
        (response: IApiResponse<IApiSuccessResponse<IGroupSettingVM[]>>) => {
          if (response.data.Error) {
            this.getGroupSettingByNameState.error = response.data.Message;
          } else {
            this.groupDetailsByName = response.data.Data;
            this.getGroupSettingByNameState.success = true;
          }
        }
      )
      .catch((err: string) => {
        this.getGroupSettingByNameState.error = err;
      })
      .finally(
        action(() => {
          this.getGroupSettingByNameState.inProgress = false;
        })
      );
  };

  AddGroupCodeService = (data: IAddGroupCode) => {
    this.addGroupCodeState.inProgress = true;
    const url = URLConstants.AddGroupCode;
    return baseService
      .postRequest(url, data)
      .then((response: IApiResponse<IApiSuccessResponse<boolean>>) => {
        if (response.data.Error) {
          this.addGroupCodeState.error = response.data.Message;
        } else {
          this.addGroupCodeState.success = true;
        }
      })
      .catch((err: string) => {
        this.addGroupCodeState.error = err;
      })
      .finally(
        action(() => {
          this.addGroupCodeState.inProgress = false;
        })
      );
  };
  //#endregion

  //#region Computed
  get allGroupSettings(): IGroupSettingList[] {
    let groupSettingData: IGroupSettingList[] = [];
    if (
      this.groupSettingList?.Trays &&
      this.groupSettingList.Trays?.length > 0
    ) {
      this.groupSettingList.Trays.map(
        ({
          TrayId,
          CustomerId,
          Id,
          EndUserName,
          EndUserOrderId,
          TenantId,
          OrderId,
          SubContractor,
          SubContractorName,
          ProductId,
          Katashiki1,
          Katashiki2,
          Kishu,
          ProcessName,
          Quantity,
          InvoiceNumber,
          QuantityUnitName,
          CustomerDueDate,
          MissingParts,
          PlanedDueDate,
          Group,
          ProductComment1,
          ProductComment2,
          ProductComment3,
          ProductComment4,
          ProductComment5,
          ProductComment6,
          CurrentState,
          Remarks,
          isSelected,
          ProductName,
          Code,
          KeyInfo1,
          KeyInfo2,
          KeyInfo3,
        }) => {
          groupSettingData.push({
            TrayId,
            CustomerId: CustomerId || '',
            Id,
            EndUserName,
            EndUserOrderId,
            TenantId,
            OrderId,
            SubContractor,
            SubContractorName,
            ProductId,
            Katashiki1,
            Katashiki2,
            Kishu,
            ProcessName,
            Quantity,
            InvoiceNumber,
            QuantityUnitName,
            MissingParts,
            Group,
            ProductComment1,
            ProductComment2,
            ProductComment3,
            ProductComment4,
            ProductComment5,
            ProductComment6,
            CurrentState,
            Remarks,
            ProductName,
            Code,
            CustomerDueDate: fixTimezoneOffsetYYYYMMDD(CustomerDueDate),
            PlanedDueDate: fixTimezoneOffsetYYYYMMDD(PlanedDueDate),
            // CustomerDueDate: convertNonUTCDateToLocalTimeZone(
            //   fixTimezoneOffset(CustomerDueDate)
            // ),
            // PlanedDueDate: convertNonUTCDateToLocalTimeZone(
            //   fixTimezoneOffset(PlanedDueDate)
            // ),
            isSelected: false,
            KeyInfo1: KeyInfo1,
            KeyInfo2: KeyInfo2,
            KeyInfo3: KeyInfo3,
            NextProcess: ''
          });
        }
      );
    }
    return groupSettingData;
  }

  get groupsByNameList(): IGroupSettingVM[] {
    let groupByNameData: IGroupSettingVM[] = [];
    if (this.groupDetailsByName && this.groupDetailsByName.length > 0) {
      this.groupDetailsByName.map(
        ({
          TrayId,
          CustomerId,
          Id,
          EndUserName,
          EndUserOrderId,
          TenantId,
          OrderId,
          SubContractor,
          SubContractorName,
          ProductId,
          Katashiki1,
          Katashiki2,
          Kishu,
          ProcessName,
          Quantity,
          InvoiceNumber,
          QuantityUnitName,
          CustomerDueDate,
          MissingParts,
          PlanedDueDate,
          Group,
          ProductComment1,
          ProductComment2,
          ProductComment3,
          ProductComment4,
          ProductComment5,
          ProductComment6,
          CurrentState,
          Remarks,
          isSelected,
          ProductName,
          Code,
          KeyInfo1,
          KeyInfo2,
          KeyInfo3
        }) => {
          groupByNameData.push({
            TrayId,
            CustomerId: CustomerId || '',
            Id,
            EndUserName,
            EndUserOrderId,
            TenantId,
            OrderId,
            SubContractor,
            SubContractorName,
            ProductId,
            Katashiki1,
            Katashiki2,
            Kishu,
            ProcessName,
            Quantity,
            InvoiceNumber,
            QuantityUnitName,
            MissingParts,
            Group,
            ProductComment1,
            ProductComment2,
            ProductComment3,
            ProductComment4,
            ProductComment5,
            ProductComment6,
            CurrentState,
            Remarks,
            isSelected,
            ProductName,
            CustomerDueDate: fixTimezoneOffsetYYYYMMDD(CustomerDueDate),
            PlanedDueDate: fixTimezoneOffsetYYYYMMDD(PlanedDueDate),
            Code,
            KeyInfo1: KeyInfo1 || 'N/A',
            KeyInfo2: KeyInfo2 || 'N/A',
            KeyInfo3: KeyInfo3 || 'N/A'
          });
        }
      );
    }
    return groupByNameData;
  }

  get allGroupOptions(): IOption[] {
    let groupData: IOption[] = [];
    if (this.groupSettingOptions && this.groupSettingOptions?.length > 0) {
      this.groupSettingOptions.map((group, index) => {
        groupData.push({
          id: index,
          value: group.TrayGroupName,
          disabled: false, //group.InActive,
        });
      });
    }
    return groupData;
  }
  get isGroupActive(): boolean {
    return (
      this.groupDetailsByName?.some(
        ({ CurrentState = '' }) => CurrentState.toLowerCase() !== 'pending'
      ) ?? false
    );
  }
  //#endregion

  //#region Reset
  reset = () => {
    this.groupSettingList = GetAllGroupSettingState;
  };

  resetGroupSettingDetail = () => {
    this.groupSetting = undefined;
    this.groupSettingState = { ...this.initialStateValue };
  };

  resetDeleteGroupSetting = () => {
    this.deleteGroupSettingState = { ...this.initialStateValue };
  };

  resetAddUpdateGroupSettingState = () => {
    this.addUpdateGroupSettingState.inProgress = false;
    this.addUpdateGroupSettingState.success = false;
    this.addUpdateGroupSettingState.error = '';
  };

  resetGroupSettingByNameState = () => {
    this.getGroupSettingByNameState = { ...this.initialStateValue };
  };

  resetGetAllGroupSettingState = () => {
    this.getAllGroupSettingState.success = false;
  };

  resetUpdateGroupSettingState = () => {
    this.updateGroupSettingState = { ...this.initialStateValue };
  };

  resetAddGroupCodeState = () => {
    this.addGroupCodeState = { ...this.initialStateValue };
  };

  resetGetAllGroupNameSettingState = () => {
    this.getAllGroupNameSettingState = { ...this.initialStateValue };
  };

  /**
   * This function is used to reset all store observables to their initial values.
   * @returns
   */
  resetStore = () => {
    this.groupSetting = undefined;
    this.groupSettingState = { ...this.initialStateValue };
    this.groupSettingList = GetAllGroupSettingState;
    this.addUpdateGroupSettingState = { ...this.initialStateValue };
    this.deleteGroupSettingState = { ...this.initialStateValue };
    this.getAllGroupSettingState = { ...this.initialStateValue };
    this.groupSettingOptions = [];
    this.getGroupSettingByNameState = { ...this.initialStateValue };
    this.groupDetailsByName = [];
    this.updateGroupSettingState = { ...this.initialStateValue };
    this.addGroupCodeState = { ...this.initialStateValue };
  };
  //#endregion
}

export default new GroupSettingStore();
